import { Injectable } from '@angular/core';
import { defer, Observable, of, throwError, timer } from 'rxjs';
import { delayWhen, filter, mergeMap, retryWhen, scan, take } from 'rxjs/operators';
import { BaseResponse } from '../../models/BaseResponse.model';
import { NetworkStatusService } from '../../shared/services/network-status/network-status.service';

interface RetryableBusinessFailure<T> {
  isRetryableBusinessFailure: true;
  response: T;
}

@Injectable({
  providedIn: 'root'
})
export class RequestRetryService {
  private readonly attemptsPerGroup = 3;
  private readonly retryDelay = 1000;
  private readonly retryGroupDelay = 10000;

  constructor(private networkStatus: NetworkStatusService) {}

  // Recreates retryable requests in two groups of three attempts separated by ten seconds.
  execute<T extends BaseResponse>(request: () => Observable<T>): Observable<T> {
    const request$ = defer(() => request()) as Observable<T>;

    return request$.pipe(
      mergeMap(response => {
        // Business failures are normal emissions, so convert only retryable ones to retry signals.
        if (this.shouldRetryResponse(response)) {
          return throwError({ isRetryableBusinessFailure: true, response } as RetryableBusinessFailure<T>);
        }

        return of(response);
      }),
      retryWhen(errors => errors.pipe(
        scan((attempts, error) => {
          if (!this.shouldRetryError(error) || attempts >= this.attemptsPerGroup * 2 - 1) {
            throw error;
          }

          return attempts + 1;
        }, 0),
        delayWhen(attempts => this.waitForRetry(attempts))
      ))
    );
  }

  // Server-declared failures are retried only when no dedicated popup or expiry flow exists.
  private shouldRetryResponse(response: BaseResponse): boolean {
    return response?.IsSuccessful === false && !response.IsPopup && !response.PopUpMessageModel && !this.isExpired(response);
  }

  // Transport failures can contain the same server error payload as successful HTTP responses.
  private shouldRetryError(error: any): boolean {
    if (this.isRetryableBusinessFailure(error)) {
      return this.shouldRetryResponse(error.response);
    }

    const response = error?.error || error;
    return !response?.IsPopup && !response?.PopUpMessageModel && !this.isExpired(response);
  }

  // Identifies the internal signal used to retry a business-level failure.
  private isRetryableBusinessFailure(error: any): error is RetryableBusinessFailure<BaseResponse> {
    return error?.isRetryableBusinessFailure === true;
  }

  // Resume on network restoration; otherwise wait one second, or ten seconds between retry groups.
  private waitForRetry(attempts: number): Observable<number | boolean> {
    if (!this.networkStatus.isOnline) {
      return this.networkStatus.onlineStatus$.pipe(
        filter(isOnline => isOnline),
        take(1)
      );
    }

    const delay = attempts === this.attemptsPerGroup ? this.retryGroupDelay : this.retryDelay;
    return timer(delay);
  }

  // Expired sessions retain their existing navigation and messaging behavior.
  private isExpired(response: BaseResponse): boolean {
    return !!response && (response.ErrorCode === 'RNF01' || (response.ErrorMessage || '').includes('פג תוקף'));
  }
}